import { useState, Fragment, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, Button, Dropdown, Space, Badge, Layout, Typography } from "antd";
import { BellOutlined, UserOutlined, UserAddOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";

import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "store/hook";
import { setStateCid, setIsLogin, setLogPath } from "../store/globalSlice";
import { setFilter } from "Components/Topology/Device/deviceTableSlice";
import { useListCustomerInf } from "Components/Management/queries";
import { useLogout } from "Components/Login/quires";
import { useAlarmLogList, useCurrentAlarmList } from "Components/Topology/queries";
import { useUpdateUserSetting, useSetting, useUpdateSuperSetting } from "./queries";
import { GetUrlPath } from "Utility/hooks/url";

// import useDarkMode from "use-dark-mode";
import UserSettingModal from "./Components/UserSetting";
import CompanyMainLogo from "image/CompanyMainLogo.png";
import LoginLogo from "image/LoginLogo.png";
import styles from "Components/Header/header.module.scss";
import ChangePasswordModal from "./Components/ChangePassword";
import AlarmLogList from "./Components/AlarmLogList";
import { usePreference } from "./Components/usePreference";
import { useQueryClient } from "react-query";

type MenuItem = Required<MenuProps>["items"][number];

const { Header: AntdHeader } = Layout;

// 默認設置
const defaultSetting = {
  language: "en",
  ticket_language: "en",
  dark_mode: false,
  time_zone: 0,
  date_joined: 0,
  company: "",
  noti_list_receive_time: 0,
  notification_receive_time: 0,
  timeout: 5,
};

const Header = () => {
  const pathname = GetUrlPath().split("/")[1];
  //從 Redux store 中獲取一些全域狀態，如 isLogin、level、stateCid、user 和 loginCid。
  const { isLogin, level, stateCid, user, loginCid } = useAppSelector((state) => state.global);

  const disp = useAppDispatch();
  // is_super_super = 用戶級別 level，確定是否為 "super_super" 級別。  *是為 true 否為 false*
  const is_super_super = level === "super_super";
  const getCid = () => {
    let cid = loginCid;
    if (level === "super_super") {
      cid = stateCid === "" ? loginCid : stateCid;
    }
    return cid;
  };
  const is_super_super_Login = is_super_super && isLogin;
  const {
    data: {
      language,
      ticket_language,
      dark_mode,
      time_zone,
      date_joined,
      company,
      noti_list_receive_time: rt,
      notification_receive_time,
      timeout,
      mail_server_active,
      mail_server,
      mail_port,
      mail_from,
      mail_from_alias,
      mail_token,
      openapi_token,
    } = defaultSetting,
  } = useSetting(user, level, isLogin);
  usePreference({ language, ticket_language, dark_mode, level, time_zone, isLogin });
  const { mutate: updateUserSettingMutation } = useUpdateUserSetting();
  const { mutate: updateSuperSettingMutation } = useUpdateSuperSetting();
  //notification bell onclick time
  const receive_time = (is_super_super ? notification_receive_time?.[getCid()] : notification_receive_time) || 0;
  //notification list close time
  const noti_list_receive_time = (is_super_super ? rt?.[getCid()] : rt) || 0;
  //store Bell on click time then set as noti_list_receive_time after list closed
  const [BellonClickTime, setBellonClickTime] = useState<any>(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [ChangePassVisible, setChangePassVisible] = useState(false);
  const { data: customerInf } = useListCustomerInf(level, stateCid, isLogin);
  const customerInfList = customerInf?.response || [];
  const { mutateAsync: logoutMutation } = useLogout();
  const queryClient = useQueryClient();

  // FIXME: start -- 關閉小鈴噹 --
  // const { data: alarmLogList = [] } = useAlarmLogList(stateCid, level, isLogin);
  // const { data: currentAlarmList = [] } = useCurrentAlarmList(stateCid, level, isLogin);

  // const log_unRead = alarmLogList.filter((item: any) => item?.trigger_time >= receive_time).length;
  // const current_unRead = currentAlarmList.filter((item: any) => item?.trigger_time >= receive_time).length;
  // const unRead: number = log_unRead + current_unRead;
  // FIXME: end -- 關閉小鈴噹 --

  const [userSettingVisible, setUserSettingVisible] = useState(false);

  useEffect(() => {
    if (level === "super_super" && userSettingVisible) {
      queryClient.invalidateQueries(["setting"]);
    }
  }, [level, userSettingVisible]);
  // 用於登出用戶，會發送登出請求，清除本地存儲的用戶資訊，並導航到登入頁面。
  const userLogout = () => {
    logoutMutation().then(() => {
      disp(setIsLogin(false));
      localStorage.removeItem("authUser.name");
      localStorage.removeItem("authUser.cid");
      localStorage.removeItem("authUser.level");
      localStorage.removeItem("authUser.openapiToken");
      navigate("/userlogin");
    });
  };
  // 用於處理選單點擊事件，包括修改密碼、設置設置和登出。
  const handleChange: MenuProps["onClick"] = ({ key }) => {
    if (key === "changePassword") {
      return setChangePassVisible(true);
    }
    if (key === "mySetting") {
      return setUserSettingVisible(true);
    }
    if (key === "logout") {
      return userLogout();
    }

    disp(setStateCid(key));
  };
  // 用於處理警報通知點擊事件，根據警報內容導航到相應的頁面。
  const onAlarmCkick = (item: any) => {
    const name_or_id = item.name !== "" ? item.name : item.id;
    disp(setFilter({ name_or_id: [name_or_id] }));
    navigate("./overview");
  };

  // super_super 才可以做 my setting 設定
  const defaultMenu =
    stateCid === ""
      ? [
          { label: t("ISMS.Change Password"), key: "changePassword" },
          { label: t("ISMS.My Setting"), key: "mySetting" },
          { label: t("ISMS.LogOut"), key: "logout" },
        ]
      : [
          { label: t("ISMS.Change Password"), key: "changePassword" },
          { label: t("ISMS.LogOut"), key: "logout" },
        ];

  const list = [
    // ...defaultMenu 使用展開運算符，將 defaultMenu 中，原本定義的 stateCid 判斷後顯示內容 擴展 list 中。
    ...defaultMenu,
    {
      // 表示一個菜單選項的分隔線
      type: "divider",
    },
    { label: "Super_Super", key: "" },
    ...customerInfList.map(({ cid, inf_list: { company } }) => getItem(company, cid)),
    // { label: "Admin Setting", key: "adminSetting" }, //admin settung: port for mail server, session timeout
  ];
  //@ts-ignore
  // 等級為 "super_super" 顯示 list ，沒有則顯示 預設 defaultMenu
  const items: MenuItem[] = is_super_super_Login ? list : defaultMenu;

  const userMenu = <Menu onClick={handleChange} items={items} />;
  return pathname === "osmartGo" || pathname === "osmartGO" ? (
    <></>
  ) : isLogin ? (
    <AntdHeader>
      <UserSettingModal
        userSettingVisible={userSettingVisible}
        setUserSettingVisible={setUserSettingVisible}
        language={language}
        ticket_language={ticket_language}
        dark_mode={dark_mode}
        time_zone={time_zone}
        date_joined={date_joined}
        company={company}
        timeout={timeout}
        mail_server_active={mail_server_active}
        mail_server={mail_server}
        mail_port={mail_port}
        mail_from={mail_from}
        mail_from_alias={mail_from_alias}
        mail_token={mail_token}
        openapi_token={openapi_token}
      />

      <ChangePasswordModal ChangePassVisible={ChangePassVisible} setChangePassVisible={setChangePassVisible} />
      <header className={styles.header}>
        <Space size={1}>
          <img alt="" src={CompanyMainLogo} />
        </Space>
        <Space size={2}>
          <Fragment>
            {/* // FIXME: start -- 關閉小鈴噹 -- */}
            {/* <Dropdown
              trigger={["click"]}
              overlay={
                <AlarmLogList
                  onAlarmCkick={onAlarmCkick}
                  noti_list_receive_time={noti_list_receive_time}
                  currentAlarmList={currentAlarmList}
                />
              }
              overlayStyle={{ boxShadow: "1px 1px 6px lightgray" }}
              placement="bottomLeft"
              onVisibleChange={(visible) => {
                //update read time for background  list after closing the popover
                if (visible === false) {
                  if (level === "super_super") {
                    updateSuperSettingMutation({ noti_list_receive_time: BellonClickTime });
                  } else {
                    updateUserSettingMutation({ user, noti_list_receive_time: BellonClickTime });
                  }
                }
              }}
            >
              <Badge size="small" overflowCount={999} offset={[-22, 6]} count={unRead || 0}>
                <Button
                  type="text"
                  icon={<BellOutlined style={{ fontSize: "1.2rem" }} />}
                  onClick={() => {
                    const currentTime = Date.now();
                    const timestamp = Math.round(currentTime / 1000);
                    if (level === "super_super") {
                      const super_receive_time_obj = notification_receive_time || {};
                      if (stateCid !== "") {
                        super_receive_time_obj[`${stateCid}`] = timestamp;
                      } else {
                        super_receive_time_obj["osmart"] = timestamp;
                      }
                      updateSuperSettingMutation({ notification_receive_time: super_receive_time_obj });
                      setBellonClickTime(super_receive_time_obj);
                      return;
                    }
                    updateUserSettingMutation({ user, notification_receive_time: timestamp });
                    setBellonClickTime(timestamp);
                  }}
                ></Button>{" "}
              </Badge>
            </Dropdown> */}
            {/* // FIXME: end -- 關閉小鈴噹 -- */}
          </Fragment>

          <Dropdown.Button
            overlay={userMenu}
            placement="bottomRight"
            icon={level === "super_super" ? <UserAddOutlined /> : <UserOutlined />}
            trigger={["click"]}
          >
            {/* 若等級為"super_super" 名稱為 公司名稱 沒有的話就顯示 Super_Super ，若等級不是"super_super"則顯示 user ID */}
            {level === "super_super"
              ? customerInfList.find((item: any) => item.cid === stateCid)?.inf_list.company || "Super_Super"
              : user}
          </Dropdown.Button>
        </Space>
      </header>
    </AntdHeader>
  ) : (
    <></>
  );
};

export default Header;

function getItem(label: React.ReactNode, key: React.Key): MenuItem {
  return {
    key,
    label,
  } as MenuItem;
}
